import React, {
  Dispatch,
  ReactNode,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { useLocation } from "react-router";
import axios from "axios";
import classNames from "classnames";

import { ReactComponent as PlusIcon } from "src/images/plus-transparent.svg";
import { ReactComponent as MinusIcon } from "src/images/minus.svg";
import InfiniteScroll from "react-infinite-scroller";
import { showErrorToast } from "src/utils/methods";
import {
  CustomSelectOption,
  GalleryConfig,
  GalleryCreatorData,
  GalleryFetchFN,
  GalleryFilters,
} from "src/types";
import { gallerySocialProviders } from "./components/GalleryFiltersDropdown/utils";
import GalleryContentViewer from "./components/GalleryContentViewer/GalleryContentViewer";
import GalleryFiltersDropdown from "./components/GalleryFiltersDropdown/GalleryFiltersDropdown";
import {
  GalleryItem,
  GalleryListElement,
} from "./components/GalleryItem/GalleryItem";
import IDHFormattedMessage from "../IDHFormattedMessage/IDHFormattedMessage";
import { Button } from "../Button/Button";
import MemoizedLoader from "../Loader/Loader";
import GalleryNoItemsFound from "./components/GalleryNoItemsFound/GalleryNoItemsFound";
import Tooltip from "../Tooltip/Tooltip";
import { GallerySelectAll } from "./components/GallerySelectAll/GallerySelectAll";
import { GalleryToolbar } from "./components/GalleryToolbar/GalleryToolbar";
import { GallerySortByKey } from "./components/GallerySortBy/utils";
import {
  filterByWorkingImage,
  filterGalleryData,
  sortGalleryElements,
} from "./methods";
import GallerySortBy from "./components/GallerySortBy/GallerySortBy";
import SelectableTags from "../SelectableTags/SelectableTags";
import _ from "lodash";
import { useQuery } from "src/app/methods/useQuery";

import "./Gallery.scss";

interface Props {
  config: GalleryConfig;
  header: {
    title: string | ReactNode;
    keywords?: string[];
  };
  showMore: boolean;
  setShowMore?: Dispatch<SetStateAction<boolean>>;
  creatorData: GalleryCreatorData;
  enableFilters?: boolean;
  enableSortBy?: boolean;
  initialSortBy?: CustomSelectOption<GallerySortByKey>;
}

export function Gallery(props: Props) {
  const {
    config,
    header,
    showMore,
    setShowMore,
    creatorData,
    enableFilters,
    enableSortBy,
    initialSortBy,
  } = props;

  const initialFilters = {
    isMyCampaignsOnly: false,
    platforms: [...gallerySocialProviders.map((item) => item.value)],
    dateFrom: null,
    dateTo: null,
  };

  const [posts, setPosts] = useState<GalleryListElement[]>([]);
  const [filters, setFilters] = useState<GalleryFilters>(initialFilters);
  const [sortBy, setSortBy] = useState<CustomSelectOption<GallerySortByKey>>(
    initialSortBy || {
      value: "publishedDate",
      label: <IDHFormattedMessage id="ws_date" defaultMessage="Date" />,
    },
  );
  const [slideIndex, setSlideIndex] = useState<number | null>(null);
  const [selectedPostIds, setSelectedPostIds] = useState<string[]>([]);
  const [endOfList, setEndOfList] = useState(false);
  const [infiniteScrollLoading, setInfiniteScrollLoading] = useState(false);
  const [offset, setOffset] = useState(0);
  const [firstLoadFinished, setFirstLoadFinished] = useState(false);
  const [activeKeywords, setActiveKeywords] = useState<string[]>(
    header.keywords || [],
  );

  const loadingRef = useRef(false);

  const { pathname, search } = useLocation();
  const query = useQuery();
  const tab = query.get("tab") || "";

  const INITIAL_LIMIT = 3;
  const SHOW_MORE_LIMIT = config.infiniteScroll?.limit || 12;
  const limit = showMore ? SHOW_MORE_LIMIT : INITIAL_LIMIT;

  const renderShowMoreButtonContent = () => {
    if (showMore) {
      return (
        <>
          <MinusIcon />
          <IDHFormattedMessage id="ws_show_less" defaultMessage="Show less" />
        </>
      );
    }

    return (
      <>
        <PlusIcon />
        <IDHFormattedMessage id="ws_show_more" defaultMessage="Show more" />
      </>
    );
  };

  const usernamesArray = useMemo(
    () =>
      config.infiniteScroll?.socialProfiles.map((item) => ({
        [item.socialProvider.replace("_", "")]: item.socialUsername,
      })) || [],
    [config.infiniteScroll?.socialProfiles],
  );

  const usernames = useMemo(
    () =>
      // @ts-ignore
      usernamesArray.length > 0 ? Object.assign(...usernamesArray) : {},
    [usernamesArray],
  );

  const getGalleryData = useCallback(
    async (getStreamDataProps?: {
      firstLoad?: boolean;
      triggeredByFiltersChange?: boolean;
    }) => {
      if (!config.infiniteScroll) return;

      const { firstLoad, triggeredByFiltersChange } = getStreamDataProps || {};

      // Use ref to prevent race conditions
      if (loadingRef.current || endOfList || (!firstLoad && !showMore)) return;

      loadingRef.current = true;
      setInfiniteScrollLoading(true);

      const offsetParam = triggeredByFiltersChange ? 0 : offset;

      try {
        const url = new URL(config.infiniteScroll.url);
        url.searchParams.append("limit", limit.toString());
        url.searchParams.append("offset", offsetParam.toString());
        url.searchParams.append("sortBy", sortBy.value.toString());

        const socialProviders = filters.platforms.filter((item) =>
          config.infiniteScroll?.socialProfiles.find(
            (profile) => profile.socialProvider.replace("_", "") === item,
          ),
        );

        const { data } = await axios.post(url.toString(), {
          usernames,
          socialProviders,
          dateFrom: filters.dateFrom,
          dateTo: filters.dateTo,
          isMyCampaignsOnly: filters.isMyCampaignsOnly,
          ...config.infiniteScroll.additionalFilters,
        });

        const newPosts = data.content.posts;

        if (newPosts.length < limit) {
          setEndOfList(true);
        }

        if (!newPosts || newPosts.length === 0) {
          return;
        }

        const filteredNewPosts = await filterByWorkingImage(newPosts);

        if (triggeredByFiltersChange) {
          setPosts(filteredNewPosts);
          setOffset(filteredNewPosts.length);
        } else {
          setPosts((prevPosts) => [...prevPosts, ...filteredNewPosts]);
          setOffset((prevOffset) => prevOffset + filteredNewPosts.length);
        }
      } catch (err) {
        setEndOfList(true);
        showErrorToast();
      } finally {
        loadingRef.current = false;
        setInfiniteScrollLoading(false);
      }
    },
    [
      config.infiniteScroll,
      endOfList,
      showMore,
      offset,
      limit,
      sortBy.value,
      filters,
      usernames,
    ],
  );

  const fetchData: GalleryFetchFN = (props) => {
    if (config.fullFetch) {
      config.fullFetch.getData();
    } else {
      getGalleryData(props);
    }
  };

  useEffect(() => {
    fetchData({
      firstLoad: true,
    });
    setFirstLoadFinished(true);
    // eslint-disable-next-line react-compiler/react-compiler
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const additionalFiltersKey = JSON.stringify(
    config.infiniteScroll?.additionalFilters ?? null,
  );

  useEffect(() => {
    if (!firstLoadFinished || !config.infiniteScroll) return;

    // Reset all state
    setPosts([]);
    setOffset(0);
    setEndOfList(false);
    loadingRef.current = false;

    fetchData({ triggeredByFiltersChange: true });
    // eslint-disable-next-line react-compiler/react-compiler
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    filters.dateFrom,
    filters.dateTo,
    filters.isMyCampaignsOnly,
    filters.platforms,
    sortBy.value,
    additionalFiltersKey,
  ]);

  useEffect(() => {
    setSelectedPostIds([]);
  }, [showMore]);

  useEffect(() => {
    if (!config.fullFetch?.data) return;

    const newKeywords = header?.keywords?.filter((keyword) =>
      config.fullFetch.data.find((el) =>
        el.keywords?.map(String).includes(keyword),
      ),
    );

    setActiveKeywords(newKeywords || []);
  }, [header?.keywords, config.fullFetch?.data]);

  useEffect(() => {
    if (!config.fullFetch?.data || selectedPostIds.length === 0) return;

    const filteredSelectedPostIds = selectedPostIds.filter((id) => {
      const item = config.fullFetch?.data.find((el) => el.url === id);
      if (!item) return false;

      return item.keywords?.some((el) => activeKeywords.includes(String(el)));
    });

    if (!_.isEqual(selectedPostIds, filteredSelectedPostIds)) {
      setSelectedPostIds(filteredSelectedPostIds);
    }
    // eslint-disable-next-line react-compiler/react-compiler
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config.fullFetch?.data, activeKeywords]);

  const getMoreData = useCallback(() => {
    if (!loadingRef.current) {
      getGalleryData();
    }
  }, [getGalleryData]);

  const constructData = () => {
    if (config.fullFetch?.data) {
      if (enableFilters)
        return (
          filterGalleryData(
            config.fullFetch?.data,
            filters,
            activeKeywords,
            header?.keywords,
          ) || posts
        );

      return config.fullFetch?.data || posts;
    }

    return posts;
  };

  const data = constructData();

  const sortedData = sortGalleryElements({ data, key: sortBy.value, config });
  const dataToRender = showMore ? sortedData : sortedData.slice(0, 3);

  const getScrollParentClassName = () => {
    // Creator Search
    if (tab === "creatorSearch") {
      return "pane__container-wrap";
    }

    // CDT
    if (pathname.includes("/creator-discovery-tool")) {
      return "pane__container-wrap";
    }

    if (pathname.includes("-insights")) {
      return "id-gallery-pane__content";
    }

    // Creator CRM
    if (
      pathname.includes("/creator-database") ||
      search.includes("displayGlobalTask")
    ) {
      return "global-task-details__content";
    }

    // Campaign View
    if (pathname.includes("/projects")) {
      return "task__container";
    }

    // Some default value
    return "pane__container-wrap";
  };

  const fetchLoading = config.fullFetch?.loading;
  const anyPostsSelected = selectedPostIds.length > 0;
  const hasMore = (!config.fullFetch && !endOfList) || fetchLoading;
  const loading = fetchLoading || infiniteScrollLoading;
  const noItems = !loading && !dataToRender.length;

  return (
    <div
      className={classNames("gallery", {
        "gallery--show-more": showMore,
      })}
    >
      <div className="gallery__header">
        <div className="gallery__header-title">
          <span>{header.title}</span>
          <SelectableTags
            feed={header.keywords || []}
            value={activeKeywords}
            onChange={setActiveKeywords}
            displayWithoutWrapper
          />
        </div>
        {setShowMore && (
          <Button
            className="gallery__show-more-less-button"
            variant="grey"
            size="medium"
            onClick={() => setShowMore((v) => !v)}
            dataQa={showMore ? "show-less-button" : "show-more-button"}
          >
            {renderShowMoreButtonContent()}
          </Button>
        )}
      </div>

      <div className="gallery__toolbar">
        {showMore && (
          <>
            <GallerySelectAll
              posts={sortedData}
              selectedPostIds={selectedPostIds}
              setSelectedPostIds={setSelectedPostIds}
            />
            {enableFilters && (
              <GalleryFiltersDropdown
                filters={filters}
                setFilters={setFilters}
                initialFilters={initialFilters}
              />
            )}
            {enableSortBy && (
              <GallerySortBy sortBy={sortBy} setSortBy={setSortBy} />
            )}
          </>
        )}

        {dataToRender.length > 1 ? (
          <Tooltip
            content={
              <div className="gallery__tooltip-content">
                <h6>
                  <IDHFormattedMessage
                    id="ws_you_cant_see_thumbnails_because"
                    defaultMessage="You can’t see thumbnails because"
                  />
                  :
                </h6>

                <ul>
                  <li>
                    <IDHFormattedMessage
                      id="ws_creator_blocked_post_preview"
                      defaultMessage="Creator blocked post preview"
                    />
                  </li>
                  <li>
                    <IDHFormattedMessage
                      id="ws_post_contains_some_restricted_content"
                      defaultMessage="Post contains some restricted content"
                    />
                  </li>
                  <li>
                    <IDHFormattedMessage
                      id="ws_post_is_too_old"
                      defaultMessage="Post is too old"
                    />
                  </li>
                </ul>
              </div>
            }
            clickable
          >
            <span className="gallery__tooltip-button">
              <IDHFormattedMessage
                id="ws_why_cant_i_see_thumbnails"
                defaultMessage="Why can’t I see thumbnails"
              />
              ?
            </span>
          </Tooltip>
        ) : null}
      </div>

      {noItems && (
        <GalleryNoItemsFound
          filters={filters}
          setFilters={setFilters}
          initialFilters={initialFilters}
        />
      )}

      <InfiniteScroll
        pageStart={0}
        loadMore={getMoreData}
        className={classNames("gallery__list", {
          "gallery__list--additional-bottom-padding": anyPostsSelected,
        })}
        hasMore={hasMore}
        loader={
          showMore ? (
            <div className="gallery__list-loader">
              <MemoizedLoader />
            </div>
          ) : (
            <></>
          )
        }
        useWindow={false}
        getScrollParent={() =>
          document.querySelector(
            `.${getScrollParentClassName()}`,
          ) as HTMLElement
        }
      >
        {dataToRender.map((item, index) => (
          <GalleryItem
            data={item}
            index={index}
            setSlideIndex={setSlideIndex}
            displayCheckbox={showMore}
            selectedPostIds={selectedPostIds}
            setSelectedPostIds={setSelectedPostIds}
          />
        ))}
      </InfiniteScroll>

      {anyPostsSelected && (
        <GalleryToolbar
          selectedPostIds={selectedPostIds}
          setSelectedPostIds={setSelectedPostIds}
        />
      )}

      <GalleryContentViewer
        data={sortedData}
        slideIndex={slideIndex}
        clearSlideIndex={() => setSlideIndex(null)}
        creatorData={creatorData}
        keywordsToHighlight={activeKeywords}
      />
    </div>
  );
}
